import Modal from 'antd/lib/modal/Modal';
import { Button, Tag, Collapse, Spin, List, Tooltip } from 'antd';
import { DownloadOutlined, PaperClipOutlined } from '@ant-design/icons';
import { IProcessItem } from '../../interfaces/Workflow';
import moment from 'moment-timezone';
import MomentTimezoneService from '../../services/moment-timezone/MomentTimezoneService';
import { ContextApp } from '../../contexts/ContextApp';
import { useContext, useEffect, useState } from 'react';
import './ViewMyProcessesModal.less';
import GraphqlService from '../../services/graphql/GraphqlService';

interface ViewMyProcessesModalProps {
  state: any;
  onCancel?: () => void;
  data: IProcessItem | null;
  getActivities: (processInstanceId: string) => Promise<{
    data: IProcessItem[];
    success: boolean;
    total?: number;
  }>;
}

interface IWorkflowFile {
  id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  url: string;
  its: string;
}

const { Panel } = Collapse;

const ViewMyProcessesModal = (props: ViewMyProcessesModalProps) => {
  const { state, onCancel, data, getActivities } = props;
  const { t } = useContext(ContextApp);
  const { getDate, getDateWithTime } = MomentTimezoneService();
  const [activities, setActivities] = useState<IProcessItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<Record<string, IWorkflowFile[]>>({});
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});
  const { customRequest, Query } = GraphqlService();
  
  const formattedInitialDate = getDate({ element: moment(data?.its).toDate() });
  const formattedDueDate = getDate({
    element: moment(data?.end_date_instance).toDate(),
  });

  const fetchFilesForTask = async (taskId: string) => {
    if (!taskId || !data?.id) return;
    
    setLoadingFiles(prev => ({ ...prev, [taskId]: true }));
    try {
      const response = await customRequest({
        query: Query.getWorkflowFilesByTask,
        variables: {
          input: {
            taskId: taskId,
            workflowInstanceId: data.id
          }
        }
      });
      
      // Check for errors in the response
      if (response?.errors) {
        console.error('GraphQL Errors:', response.errors);
        throw new Error('Error al cargar los archivos');
      }
      
      // Check if we have the expected data structure
      const filesData = response?.files || [];
      setFiles(prev => ({
        ...prev,
        [taskId]: filesData
      }));
    } catch (error) {
      console.error('Error fetching files:', error);
      // You might want to show an error message to the user here
    } finally {
      setLoadingFiles(prev => ({ ...prev, [taskId]: false }));
    }
  };

  useEffect(() => {
    const fetchActivities = async () => {
      if (data?.id) {
        setLoading(true);
        const response = await getActivities(data.id);
        setActivities(response.data ?? []);
        setLoading(false);
      }
    };
    fetchActivities();
  }, [data?.id]);

  const handlePanelChange = (key: string | string[]) => {
    const keys = Array.isArray(key) ? key : [key];
    keys.forEach(taskId => {
      if (taskId && !files[taskId] && !loadingFiles[taskId]) {
        fetchFilesForTask(taskId);
      }
    });
  };

  return (
    <Modal
      footer={false}
      open={state}
      onCancel={onCancel}
      title={'Detalle del flujo'}
      width={800}
    >
      <div className="main-container-detail-process">
        <div className="title-and-tag-container">
          <h5 className="process-title">{data?.process}</h5>

          <div className="status-container">
            <span style={{ marginRight: 10 }}>{t('entity.status')}:</span>
            <Tag color={data?.status.color}>{data?.status.name}</Tag>
          </div>
        </div>
        <span className="id-style">ID: {data?.id}</span>

        <div className="initial-date-container">
          <label className="date-label">{t('entity.startdate')}:</label>
          <span>{formattedInitialDate}</span>
        </div>

        <div className="end-date-container">
          <label className="date-label">{t('entity.limitDate')}:</label>
          <span>
            {data?.end_date_instance ? formattedDueDate : t('entity.unasignee')}
          </span>
        </div>

        <div style={{ margin: '15px 0 10px 0' }}>
          <span style={{ marginRight: '15px' }}>Motivo:</span>
          <span>{data?.reason ? data?.reason : t('entity.noReason')}</span>
        </div>

        <div style={{ margin: '15px 0 10px 0' }}>
          <label className="label-process-details ">
            {t('entity.lastActivity')}:
          </label>
          <span>{data?.activity}</span>
        </div>

        <div style={{ margin: '10px 0' }}>
          <label className="label-process-details ">
            {t('entity.asignTo')}:
          </label>
          <span>{data?.asignTo}</span>
        </div>
      </div>
      
      <Collapse className="collapse-panel">
        <Panel header="Ver Historial" key="main-history">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <Spin size="default" />
            </div>
          ) : (
            <Collapse onChange={handlePanelChange}>
              {activities.map((act: any) => {
                const dateWithTime = getDateWithTime({ element: act?.its });
                const header = act.activity;
                const taskId = act.id;
                const taskFiles = files[taskId] || [];
                const isLoading = loadingFiles[taskId];

                return (
                  <Panel 
                    header={<b>{header}</b>} 
                    key={taskId}
                    extra={
                      <Tag color={act.status?.color}>{act.status?.name}</Tag>
                    }
                  >
                     {/* <p>
                      <b>{t('entity.asignTo')}:</b>{' '}
                      {act.asignTo
                        ? act.asignTo
                        : '-'}
                    </p> */}   
                    <div style={{ marginBottom: '16px' }}>
                      <p><b>Fecha:</b> {dateWithTime}</p>
                      <p>
                        <b>{t('entity.observations')}:</b>{' '}
                        {act.comment || 'No se han dejado observaciones'}
                      </p>
                      
                      <div style={{ marginTop: '16px' }}>
                        <label className="label-info-styles">
                          <b><PaperClipOutlined /> {t('entity.attachments')}:</b>
                        </label>
                        {isLoading ? (
                          <div style={{ textAlign: 'center', padding: '8px 0' }}>
                            <Spin size="small" />
                          </div>
                        ) : taskFiles.length > 0 ? (
                          <List
                            size="small"
                            dataSource={taskFiles}
                            renderItem={(file: IWorkflowFile) => (
                              <List.Item>
                                <span style={{ color: '#999', fontSize: '12px' }}>
                                  {moment(file.its).format('DD/MM/YYYY HH:mm')}
                                </span>
              
                                <Tooltip title={file.original_filename || file.filename}>
                                  <span
                                    style={{
                                      maxWidth: 350,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1,
                                      paddingLeft: '20px',
                                    }}
                                  >
                                    {file.original_filename || file.filename}
                                  </span>
                                </Tooltip>
              
                                <Tooltip title={t('entity.download')}>
                                  <Button
                                    type="text"
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}                    
                                  >
                                    <DownloadOutlined style={{ color: '#03729F' }} />
                                  </Button>
                                </Tooltip>
                              </List.Item>
                            )}
                          />
                        ) : (
                          <p>No hay archivos adjuntos</p>
                        )}
                      </div>
                    </div>             
                  </Panel>
                );
              })}
            </Collapse>
          )}
        </Panel>
      </Collapse>
      <div className="button-close">
        <Button onClick={onCancel} type="primary">
          {t('action.close')}
        </Button>
      </div>
    </Modal>
  );
};

export default ViewMyProcessesModal;
